import { z } from 'zod';
import { formatReadexUserPrompt, promptAttachmentMentions, promptAttachmentOutlineContexts,
  type AcceptedConversationAttachment, type ChatMessage, type CodexNotification, type ReadexPromptPDFOutlineContext } from '@reados/contracts';
import type { FileRecord } from '@reados/contracts';
import type { ResolvedReference } from './references';

const libraryPath=z.string().startsWith('/workspace/library/').max(4096)
  .refine(path=>!/[\u0000-\u001f\u007f\\]/.test(path)&&path.slice(1).split('/').every(part=>!!part&&part!=='.'&&part!=='..'));

const chatAttachmentPath=z.string().regex(/^\/workspace\/\.reados\/chat-attachments\/[a-f0-9-]{36}\/[^/\\\u0000-\u001f\u007f]+$/).max(4096);
const attachmentPath=z.union([libraryPath,chatAttachmentPath]);

// Check depth before descending. A recursive parser followed by a refinement
// can overflow first on an invalid persisted receipt. These are snapshots,
// never paths or outline trees supplied by an iframe click.
function validOutlineContext(value:unknown):value is ReadexPromptPDFOutlineContext {
  if(!value||typeof value!=='object'||Array.isArray(value))return false;
  const context=value as Record<string,unknown>;
  if(Object.keys(context).some(key=>!['filePath','selectedNode'].includes(key))||!libraryPath.safeParse(context.filePath).success)return false;
  const pending=[{value:context.selectedNode,depth:1}],seen=new Set<object>();let count=0,characters=0;
  while(pending.length){
    const current=pending.pop()!;
    if(current.depth>32||++count>4096||!current.value||typeof current.value!=='object'||Array.isArray(current.value)||seen.has(current.value))return false;
    seen.add(current.value);
    const node=current.value as Record<string,unknown>;
    if(Object.keys(node).some(key=>!['title','bookPageRange','children'].includes(key))
      ||typeof node.title!=='string'||!node.title.trim()||node.title.length>4096
      ||typeof node.bookPageRange!=='string'||!node.bookPageRange.trim()||node.bookPageRange.length>4096
      ||(node.children!==undefined&&(!Array.isArray(node.children)||node.children.length>4096)))return false;
    characters+=node.title.length+node.bookPageRange.length;if(characters>120_000)return false;
    for(const child of (node.children??[]) as unknown[])pending.push({value:child,depth:current.depth+1});
  }
  return true;
}

const attachment = z.object({
  id:z.string().uuid(),kind:z.enum(['file','folder','pdf_outline_context']),resourceId:z.string().uuid(),
  name:z.string().min(1).max(4096),sandboxPath:attachmentPath,
  mimeType:z.string().max(256).optional(),fileKind:z.enum(['pdf','text','video','image','other']).optional(),
  size:z.number().int().nonnegative().optional(),contentRevision:z.string().regex(/^[a-f0-9]{64}$/).optional(),
  outlineEntryId:z.string().min(1).max(512).optional(),
  pdfOutlineContext:z.custom<ReadexPromptPDFOutlineContext>(validOutlineContext).optional(),
}).strict().refine(item=>libraryPath.safeParse(item.sandboxPath).success||item.kind==='file'&&item.sandboxPath.startsWith(`/workspace/.reados/chat-attachments/${item.resourceId}/`)).refine(item=>item.kind==='pdf_outline_context'
  ?!!item.outlineEntryId&&!!item.pdfOutlineContext&&item.pdfOutlineContext.filePath===item.sandboxPath
  :item.outlineEntryId===undefined&&item.pdfOutlineContext===undefined);
const receipt = z.object({
  threadId:z.string().min(1),turnId:z.string().min(1),submissionId:z.string().uuid(),
  userMessageId:z.string().min(1).optional(),userPrompt:z.string().max(200_000),attachments:z.array(attachment).max(20),
});

/** Only accepted, identity-bound receipts can replace native model text for
 * display. Input items and assistant/DIL source are never changed. */
export function conversationInputMessages(messages:ChatMessage[],events:CodexNotification[]):ChatMessage[] {
  const accepted=new Map<ChatMessage,z.infer<typeof receipt>>();
  for(const event of events){
    if(event.method!=='reados/conversationInput')continue;
    const parsed=receipt.safeParse(event.params);if(!parsed.success)continue;
    const value=parsed.data;
    const candidates=messages.filter(message=>message.role==='user'&&message.threadId===value.threadId&&message.turnId===value.turnId
      &&(!value.userMessageId||message.id===value.userMessageId));
    if(candidates.length!==1)continue;
    const user=candidates[0],item=user.items?.find(item=>item.type==='userMessage'&&item.id===user.id);
    const nativeText=Array.isArray(item?.content)?item.content.flatMap(part=>part?.type==='text'&&typeof part.text==='string'?[part.text]:[]).join('\n'):undefined;
    // Exact equality is an integrity check after the identity join, not a guess
    // based on matching text. A manually typed prompt-shaped message is untouched.
    if(nativeText!==undefined&&nativeText!==formatReadexUserPrompt(value.userPrompt,promptAttachmentMentions(value.attachments),promptAttachmentOutlineContexts(value.attachments)))continue;
    accepted.set(user,value);
  }
  return messages.map(message=>{
    const value=accepted.get(message);
    return value?{...message,text:value.userPrompt,attachments:value.attachments,submissionId:value.submissionId}:message;
  });
}

export function transcriptAttachments(values:AcceptedConversationAttachment[]|undefined):Record<string,unknown>[] {
  return (values||[]).flatMap<Record<string,unknown>>(value=>{
    const parsed=attachment.safeParse(value);if(!parsed.success)return [];
    const item=parsed.data;
    // Exact iPad virtual attachment DTO. The context is the server-accepted
    // selection snapshot, not the current mutable PDF structure or browser path.
    if(item.kind==='pdf_outline_context')return [{id:item.id,kind:'pdf_outline_context',displayName:item.name,
      mimeType:'text/markdown',filePath:item.sandboxPath,prompt:'',pdfOutlineContext:item.pdfOutlineContext,
      markdown:formatReadexUserPrompt('',[],[item.pdfOutlineContext!]),
    }];
    if(item.kind==='file'&&(item.fileKind==='image'||item.mimeType?.startsWith('image/'))&&item.contentRevision)
      return [{id:item.id,kind:'importedImage',displayName:item.name,mimeType:item.mimeType,
        resourceId:item.resourceId,resourceKind:'file',virtualPath:item.sandboxPath,contentRevision:item.contentRevision,
        // This is an identity for parent-side ticket resolution, not a URL the
        // opaque iframe may load directly. The revision binds sent previews to
        // the accepted bytes even after a file is renamed or overwritten.
        url:`/api/files/${encodeURIComponent(item.resourceId)}/content?contentRevision=${item.contentRevision}`,
      }];
    return [{id:item.id,kind:item.kind==='folder'?'directory':'workspaceItem',displayName:item.name,
      virtualPath:item.sandboxPath,resourceId:item.resourceId,resourceKind:item.kind,
      mimeType:item.kind==='folder'?'inode/directory':item.mimeType,
      ...(item.contentRevision?{contentRevision:item.contentRevision}:{}),
    }];
  });
}

/** History and the live library hydrate independently. An exact accepted image
 * snapshot already supplies the owned ID/revision needed for server authority;
 * waiting for the catalog would cache a false "missing" result in the iframe. */
export function acceptedImageReference(source:string,messages:Record<string,unknown>[],files:FileRecord[],origin:string):ResolvedReference|null {
  let url:URL;
  try { url=new URL(source,origin); } catch { return null; }
  if(url.origin!==origin||url.username||url.password||url.hash||url.searchParams.size!==1)return null;
  const path=/^\/api\/files\/([a-f0-9-]{36})\/content$/.exec(url.pathname),revision=url.searchParams.get('contentRevision');
  if(!path||!revision||!/^[a-f0-9]{64}$/.test(revision))return null;
  for(const message of messages){
    if(message.role!=='user'||!Array.isArray(message.attachments))continue;
    for(const value of message.attachments){
      if(!value||typeof value!=='object')continue;
      const attachment=value as Record<string,unknown>;
      if(attachment.kind!=='importedImage'||attachment.resourceKind!=='file'||attachment.resourceId!==path[1]
        ||attachment.contentRevision!==revision||attachment.url!==url.pathname+url.search
        ||typeof attachment.displayName!=='string'||!attachmentPath.safeParse(attachment.virtualPath).success)continue;
      const current=files.find(file=>file.id===attachment.resourceId);
      if(current&&current.contentRevision!==revision)return null;
      return {kind:'image',target:{fileId:path[1],path:current?.sandboxPath??attachment.virtualPath as string},
        label:attachment.displayName,contentRevision:revision,sourceIdentity:url.href,resourceUrl:url.href};
    }
  }
  return null;
}
